import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'react-feather';
import { Button, Dropdown, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getPaymentIssuesAPI, retryChargeAPI, waivePaymentAPI } from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './PaymentIssues.module.scss';

export default function PaymentIssues() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const [retryId, setRetryId] = useState(null);
  const [waiveId, setWaiveId] = useState(null);
  const [waiveNote, setWaiveNote] = useState('');
  const [emailOpen, setEmailOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPaymentIssuesAPI();
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { name: 'Tenant', selector: (r) => r.companyName, sortable: true, grow: 1 },
      { name: 'Issue', selector: (r) => r.issueType, sortable: true },
      { name: 'Amount', selector: (r) => `$${r.amountUsd}`, sortable: true },
      {
        name: 'Attempted',
        selector: (r) => new Date(r.attemptedOn).toLocaleString(),
        sortable: true,
      },
      { name: 'Status', selector: (r) => r.status, sortable: true },
      { name: 'Owner', selector: (r) => r.owner },
      { name: 'Next step', selector: (r) => r.nextStep, grow: 1 },
      {
        name: 'Actions',
        ignoreRowClick: true,
        cell: (r) => (
          <Dropdown>
            <Dropdown.Toggle size="sm" variant="outline-secondary">
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setRetryId(r.id)}>Retry charge</Dropdown.Item>
              <Dropdown.Item onClick={() => setEmailOpen(r)}>Contact tenant</Dropdown.Item>
              <Dropdown.Item onClick={() => setWaiveId(r.id)}>Waive</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        ),
      },
    ],
    []
  );

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Payment issues" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Billing', path: platformUrl('billing') }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  return (
    <div className={styles.pagePad}>
      <PageHeader title="Payment issues" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Billing', path: platformUrl('billing') }]} />

      {loading && !rows.length ? (
        <LoadingSkeleton rows={6} cols={4} />
      ) : (
        <DataTableWrapper
          columns={columns}
          data={rows}
          loading={false}
          keyField="id"
          showSearch={false}
          selectableRows
          onSelectedRowsChange={({ selectedRows: sr }) => {
            const m = {};
            (sr || []).forEach((r) => {
              m[r.id] = true;
            });
            setSelected(m);
          }}
        />
      )}

      {selectedIds.length > 0 && (
        <div className={styles.bottomBar}>
          <Button size="sm" onClick={() => toast.success(`Notified ${selectedIds.length} tenant(s) (simulated)`)}>
            Bulk notify ({selectedIds.length})
          </Button>
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(retryId)}
        onClose={() => setRetryId(null)}
        onConfirm={async () => {
          try {
            await retryChargeAPI(retryId);
            toast.success('Retry queued');
            setRetryId(null);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Retry charge?"
        message="This will attempt to charge the tenant again."
        confirmLabel="Retry"
      />

      <Modal show={Boolean(waiveId)} onHide={() => setWaiveId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Waive issue</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">Requires billing role (localStorage mh_payment_waive_role = 1)</p>
          <Form.Label>Note</Form.Label>
          <Form.Control as="textarea" rows={2} value={waiveNote} onChange={(e) => setWaiveNote(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            disabled={!waiveNote.trim()}
            onClick={async () => {
              try {
                await waivePaymentAPI(waiveId, waiveNote);
                toast.success('Waived');
                setWaiveId(null);
                setWaiveNote('');
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Confirm waive
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(emailOpen)} onHide={() => setEmailOpen(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Email template</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="small bg-light p-2 rounded">
            {`Subject: Payment method update needed\n\nHi ${emailOpen?.companyName || 'team'},\nWe could not process your recent invoice. Please update your card.\n`}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => { toast.success('Copied to clipboard (simulated)'); setEmailOpen(null); }}>Copy</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
